import { CameraView, useCameraPermissions } from 'expo-camera';
import { useRouter } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, BackHandler, StyleSheet, TouchableOpacity, View } from 'react-native';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import env from '@/config/env';
import { redeemAccessToken } from '@/utils/tokenRedemption';

export default function ScanPage() {
  const [permission, requestPermission] = useCameraPermissions();
  const [scanned, setScanned] = useState(false);
  const [scannedData, setScannedData] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tokenRedemptionInProgress, setTokenRedemptionInProgress] = useState(false);
  const [showCamera, setShowCamera] = useState(true);
  const router = useRouter();

  // Helper function to validate QR code
  const isValidQRCode = (data: string): boolean => {
    try {
      const url = new URL(data);
      const associatedDomain = env.associatedDomain;
      const isValidDomain = url.hostname === associatedDomain || 
                           url.hostname === `www.${associatedDomain}` ||
                           url.hostname.endsWith(`.${associatedDomain}`);
      return isValidDomain && url.pathname.startsWith('/');
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (permission?.status === 'undetermined') {
      requestPermission();
    }
  }, [permission]);

  // Cleanup effect to ensure camera is properly cleaned up
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      setShowCamera(false);
      setScanned(false);
      setScannedData(null);
      setLoading(false);
    };
  }, []);

  // Handle Android hardware back button
  useEffect(() => {
    const onBackPress = () => {
      // Reset state before navigating back
      setScanned(false);
      setScannedData(null);
      setLoading(false);
      setShowCamera(true);
      // Let the router handle the back navigation
      return false;
    };
    const subscription = BackHandler.addEventListener('hardwareBackPress', onBackPress);
    return () => subscription.remove();
  }, []);

  const handleBarCodeScanned = useCallback(async ({ data }: { data: string }) => {
    setScanned(true);
    setScannedData(data);
    setShowCamera(false); // Hide camera immediately after scan
    setLoading(true);
    
    try {
      const url = new URL(data);
      
      // Check if the URL matches the associated domain
      const associatedDomain = env.associatedDomain;
      const isValidDomain = url.hostname === associatedDomain
      
      
      if (isValidDomain && url.pathname.startsWith('/')) {
        // Valid app URL - check for token and redeem if present
        const token = url.searchParams.get('token');
        const targetPath = url.pathname + url.search + url.hash;
        
        if (token) {
          setTokenRedemptionInProgress(true);
          
          try {
            // Redeem the token and refresh data for the path
            await redeemAccessToken(token);
            
            // Only navigate if token redemption was successful
            if (url.pathname) router.replace(url.pathname as any);
            setLoading(false);
            setTokenRedemptionInProgress(false);
          } catch (error) {
            console.error('[ScanPage] Token redemption failed:', error);
            setTokenRedemptionInProgress(false);
            setLoading(false);
            return; // Stop here - don't navigate if token redemption failed
          }
        } else {
          // No token - navigate directly
          router.replace(targetPath as any);
        }
      } else {
        // Invalid domain or path
        setLoading(false);
      }
    } catch (e) {
      // Not a valid URL
      setLoading(false);
    }
  }, [router]);

  if (!permission) {
    return <ThemedView style={styles.center}><ActivityIndicator size="large" /><ThemedText>Requesting camera permission...</ThemedText></ThemedView>;
  }
  if (!permission.granted) {
    return (
      <ThemedView style={styles.center}>
        <ThemedText style={{ marginBottom: 16 }}>Camera access is required to scan QR codes.</ThemedText>
        <TouchableOpacity style={styles.button} onPress={requestPermission}>
          <ThemedText style={styles.buttonText}>Grant Camera Permission</ThemedText>
        </TouchableOpacity>
      </ThemedView>
    );
  }


  return (
    <ThemedView style={{ flex: 1 }}>
      {!scanned && showCamera ? (
        <View style={{ flex: 1 }}>
          <CameraView
            style={styles.camera}
            facing="back"
            barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
            onBarcodeScanned={handleBarCodeScanned}
          />
        </View>
      ) : (
        <ThemedView style={styles.center}>
          {loading || tokenRedemptionInProgress ? (
            <View style={{ alignItems: 'center' }}>
              <ActivityIndicator size="large" />
              <ThemedText style={{ marginTop: 16, fontSize: 16, textAlign: 'center' }}>
                {tokenRedemptionInProgress ? 'Processing access token...' : 'Processing...'}
              </ThemedText>
            </View>
          ) : (
            <>
              <ThemedText style={{ fontSize: 18, marginBottom: 12 }}>
                {scannedData && isValidQRCode(scannedData) ? 'Valid QR Code' : 'Invalid QR Code'}
              </ThemedText>
              <ThemedText selectable style={{ fontSize: 16, marginBottom: 24, textAlign: 'center' }}>
                {scannedData}
              </ThemedText>
              {scannedData && !isValidQRCode(scannedData) && (
                <ThemedText style={{ fontSize: 14, marginBottom: 24, textAlign: 'center', paddingHorizontal: 20 }}>
                  This QR code is not valid for this app. Please scan a QR code from {env.associatedDomain}
                </ThemedText>
              )}
              <TouchableOpacity style={styles.button} onPress={() => { 
                setScanned(false); 
                setScannedData(null); 
                setLoading(false); 
                setShowCamera(true);
              }}>
                <ThemedText style={styles.buttonText}>Scan Again</ThemedText>
              </TouchableOpacity>
            </>
          )}
        </ThemedView>
      )}
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  camera: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  button: {
    backgroundColor: '#222',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
    marginTop: 8,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
}); 